import { sortItemsById, sortApplicationsByPath, assert, createErrorMessage, merge } from '@aviv-unified-frontend/registry-core';

/**
 * A registry factory that returns optimized getters.
 * @param registryItems The registry items input.
 * @returns Getter methods.
 */ const createRegistry = (registryItems)=>{
    const sortedApplicationById = sortItemsById(registryItems.applications);
    const sortedApplicationByPath = sortApplicationsByPath(registryItems.applications);
    const sortedMicroFrontendById = sortItemsById(registryItems.microFrontends);
    return {
        /**
		 * Getter to retrieve applications from the registry.
		 * It sorts applications following their id (alphabetic order).
		 * @returns An ordered collection iterator containing the application items sorted by id.
		 */ getApplications () {
            return Array.from(sortedApplicationById.values());
        },
        /**
		 * Getter to retrieve micro frontends from the registry.
		 * It sorts micro frontends following their id (alphabetic order).
		 * @returns An ordered collection iterator containing the micro-frontend items sorted by id.
		 */ getMicroFrontends () {
            return Array.from(sortedMicroFrontendById.values());
        },
        /**
		 * Getter to retrieve applications from the registry mapped by paths.
		 * It guarantees the correct path order (first items are more specific than the last ones).
		 * @returns An ordered collection containing the [path, application] pair sorted by path.
		 */ getApplicationByPath () {
            return Array.from(sortedApplicationByPath.entries());
        },
        /**
		 * Getter to retrieve an application matching a given id.
		 * @returns The found application or undefined.
		 */ getApplicationFromId (id) {
            return sortedApplicationById.get(id);
        },
        /**
		 * Getter to retrieve a micro frontend matching a given id.
		 * @returns The found micro frontend or undefined.
		 */ getMicroFrontendFromId (id) {
            return sortedMicroFrontendById.get(id);
        }
    };
};
/**
 * Factory to create fake registry items merged with real registry items.
 * @param fakeItems Collection of fake items to overload the sourced registry.
 * @param registryItems Real registry object to extend from (usually the branded dev registry).
 * Can be either a partial existing item or a full new item. (@see {@link ApplicationRegistryItem})
 * @returns The merged registry with the new/overloaded items.
 */ const createFakeRegistryItems = (fakeItems, registryItems = {
    applications: {},
    microFrontends: {}
})=>{
    const newRegistry = {
        applications: {
            ...registryItems.applications
        },
        microFrontends: {
            ...registryItems.microFrontends
        }
    };
    for (const fakeItem of fakeItems){
        const { id, type } = fakeItem;
        assert(id && type, createErrorMessage({
            cause: "Missing required `id` or `type` field",
            solution: `Make sure to review the \`${JSON.stringify(fakeItem)}\` item to include all needed fields`
        }));
        if (type === "application") {
            const appId = id;
            newRegistry.applications[appId] = merge(registryItems.applications[appId], fakeItem);
        } else {
            const mfeId = id;
            newRegistry.microFrontends[mfeId] = merge(registryItems.microFrontends[mfeId], fakeItem);
        }
    }
    return newRegistry;
};

export { createFakeRegistryItems, createRegistry };
